import React, { useEffect, useMemo, useRef, useState } from "react";
import type { PageName } from '../../types';
import { Calendar as CalendarIcon, Info, X, Save, Trash2, Shield, Target, Tag as TagIcon, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';

/**
 * This file transforms a basic/"ugly" trade form into the polished layout/colors
 * you've liked: dark glassy cards, indigo accents, 1–2–3 sections, live RR calc,
 * tag chips, template popover, and keyboard shortcuts (⌘/Ctrl+S, ⌘/Ctrl+K, Esc).
 *
 * It is stand‑alone: minimal UI shims below are drop‑in and can be swapped for
 * your shadcn/ui components later without changing the component structure.
 */

// ===== Minimal UI shims (swap with shadcn/ui in your app) =====
const Button = React.forwardRef(function Button({ children, onClick, disabled, variant = "default", className = "", type = "button", isDark: isDarkProp, ...props }: any, ref: any) {
    // Allow callers to pass `isDark` when the app uses per-page theme instead of
    // the global `document.documentElement` class. Fall back to checking the
    // document for backwards compatibility.
    const isDarkLocal = typeof isDarkProp === 'boolean' ? isDarkProp : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const variantClasses = variant === "primary" ? "bg-blue-600 hover:bg-blue-500 text-white" :
        variant === "default" ? "bg-indigo-600 hover:bg-indigo-500 text-white" :
        variant === "success" ? "bg-[#1cc101] hover:bg-[#17b100] text-white" :
        variant === "destructive" ? "bg-red-600 hover:bg-red-500 text-white" :
        variant === "secondary" ? (isDarkLocal ? "bg-zinc-700 hover:bg-zinc-600 text-zinc-100" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-900 border border-zinc-200") :
        variant === "outline" ? (isDarkLocal ? "bg-transparent border border-zinc-700 hover:bg-zinc-800 text-zinc-100" : "bg-transparent border border-zinc-200 hover:bg-zinc-50 text-zinc-700") :
        (isDarkLocal ? "bg-zinc-700/50 hover:bg-zinc-700 text-zinc-100" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-900");

    // Make buttons single-line with icon on the left and label on the right.
    // `inline-flex items-center gap-2 whitespace-nowrap` enforces icon-left + text-right
    // and prevents vertical wrapping; `min-w-0` allows children to truncate as needed.
    // Avoid leaking the custom prop to the DOM by not including `isDark` in
    // the spread props (we already consumed it above).
    return (
        <button ref={ref} type={type} onClick={onClick} disabled={disabled} {...props} className={`inline-flex items-center gap-2 whitespace-nowrap min-w-0 px-4 py-2 rounded-2xl text-sm transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed ${variantClasses} ${className}`}>
            {children}
        </button>
    );
});
// Card, Input, Label and Textarea will be created per-page (theme-aware) inside the page component
// Simple popover (no portal) — panel respects theme when `isDark` is passed
const SimplePopover = ({ trigger, children, isDark = false }: { trigger: React.ReactNode; children: React.ReactNode; isDark?: boolean }) => {
    const [open, setOpen] = useState(false);
    const panelClass = `absolute right-0 z-50 mt-2 w-80 rounded-xl border p-4 shadow-xl ${isDark ? 'border-zinc-700 bg-zinc-900 text-zinc-100' : 'border-zinc-200 bg-white text-zinc-900'}`;
    return (
        <div className="relative">
            <div onClick={() => setOpen(o => !o)} className="inline-block cursor-pointer">{trigger}</div>
            {open && (
                <div className={panelClass}>
                    {children}
                </div>
            )}
        </div>
    );
};
// small helpers (Tip removed — header shortcut tips were deleted)

// ===== Utilities =====
const fmtMoney = (n?: number) => (Number.isFinite(n ?? NaN) ? (n as number).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 }) : "—");

// ===== Templates & Suggestions =====
const NOTE_TEMPLATES: Record<string, string> = {
    "Opening Range Break": `Context:\n- Market bias: [Bullish/Bearish/Neutral]\n- Catalyst: [news, econ data, none]\n\nPlan:\n- Setup: ORB\n- Entry: [price] / [confirmation]\n- Stop: [price]\n- Target(s): [T1] [T2]\n- Risk: [$/R]\n\nExecution:\n- [filled/partial/slippage]\n\nReview:\n- What worked?\n- What to improve?`,
    "Pullback to VWAP": `Setup: PB to VWAP\nEntry: [trigger]\nStop: [below/above]\nTargets: [levels]\nManagement: [trail/scale]\nMistakes to avoid: [chase/late]\nPost-trade notes:`,
    "Breakout Fail (Short)": `Setup: Failed breakout\nConfirmations: [rejection wick / volume fade]\nEntry: [below retest]\nStop: [above high]\nTargets: [prior low / gap]\nPost-mortem:`,
};
const SYMBOL_SUGGESTIONS = ["AAPL","AMZN","TSLA","MSFT","NVDA","META","AMD","GOOGL","SPY","QQQ","MNQ","NQ","ES","MES"];

// Theme-aware TagInput (extracted to module scope so hooks run unconditionally)
const ThemeTagInput = ({ value, onChange, isDark, Input }: { value: string[]; onChange: (v: string[]) => void; isDark: boolean; Input: (props: Record<string, unknown>) => React.ReactElement }) => {
    const [text, setText] = useState("");
    const add = () => { const t = text.trim(); if (!t) return; if (!value.includes(t)) onChange([...value, t]); setText(""); };
    const remove = (t: string) => onChange(value.filter(v => v !== t));
    return (
        <div>
            <div className="flex gap-2">
                <Input value={text} placeholder="Add a tag and press Enter" onChange={(e: any) => setText(e.target.value)} onKeyDown={(e: any) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
                <Button isDark={isDark} onClick={add} variant="secondary" className="h-11 gap-2 inline-flex items-center justify-center rounded-xl">
                    <TagIcon size={16}/> Add
                </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                {value.map((t) => {
                    const chipClass = isDark ? 'inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-600/25 text-indigo-200 text-xs border border-indigo-500/50' : 'inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs border border-indigo-200';
                    const removeClass = isDark ? 'hover:text-red-300 ml-1' : 'hover:text-red-600 ml-1';
                    return (
                        <span key={t} className={chipClass}>
                            {t}
                            <button onClick={() => remove(t)} className={removeClass} aria-label={`remove ${t}`}><X size={12}/></button>
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

// Theme-aware SymbolCombo (extracted to module scope so hooks run unconditionally)
const ThemeSymbolCombo = ({ value, onChange, isDark, Input }: { value: string; onChange: (v: string) => void; isDark: boolean; Input: (props: Record<string, unknown>) => React.ReactElement }) => {
    const [query, setQuery] = useState(value);
    useEffect(() => setQuery(value), [value]);
    const matches = SYMBOL_SUGGESTIONS.filter(s => s.toLowerCase().includes(query.toLowerCase())).slice(0, 8);
    const listClass = `absolute z-20 mt-1 w-full rounded-xl border ${isDark ? 'border-zinc-700 bg-zinc-900 text-sm' : 'border-zinc-200 bg-white text-sm'} overflow-hidden shadow-xl`;
    return (
        <div className="relative">
            <Input value={query} onChange={(e: any) => { setQuery(e.target.value.toUpperCase()); onChange(e.target.value.toUpperCase()); }} placeholder="e.g., AAPL, MNQ, ES" />
            {query && matches.length > 0 && (
                <div className={listClass}>
                    {matches.map(m => (
                        <button key={m} onClick={() => { onChange(m); setQuery(m); }} className={`block w-full text-left px-3 py-2 ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-zinc-100'}`}>
                            {m}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Basic Inputs were implemented as a page-local shim lower in the file so the
// controls can be fully theme-aware. The top-level Input helper is intentionally
// removed to avoid duplicate definitions.
// (Label and Textarea are provided as theme-aware local versions inside the page component)

// ===== Types & Draft =====
type Side = "long" | "short";
 type AssetClass = "stock" | "futures" | "forex" | "options";
interface DraftTrade {
    dateISO: string;
    symbol: string;
    side: Side;
    assetClass: AssetClass;
    // Keep numeric fields as strings while typing to avoid caret/blur issues
    qty: string;
    entry: string;
    stop: string;
    target: string;
    strategy: string;
    tags: string[];
    notes: string;
}
const DRAFT_KEY = "trade-core-pro-v2";
import { privateTradingScopeChangedEventName, readPrivateTradingJson, writePrivateTradingJson } from '../../lib/storage/privateTradingStorage';
const defaultDraft = (): DraftTrade => ({
    dateISO: new Date().toISOString().slice(0, 10),
    symbol: "",
    side: "long",
    assetClass: "stock",
    qty: "",
    entry: "",
    stop: "",
    target: "",
    strategy: "",
    tags: ["Planned"],
    notes: "",
});
function useDraft() {
    const [draft, setDraft] = useState<DraftTrade>(() => {
        try { return JSON.parse(readPrivateTradingJson('new-trade-draft', 'local', [DRAFT_KEY]) || ""); } catch { return defaultDraft(); }
    });
    useEffect(() => { writePrivateTradingJson('new-trade-draft', JSON.stringify(draft)); }, [draft]);
    useEffect(() => {
        const resetForIdentity = () => setDraft(defaultDraft());
        window.addEventListener(privateTradingScopeChangedEventName(), resetForIdentity);
        return () => window.removeEventListener(privateTradingScopeChangedEventName(), resetForIdentity);
    }, []);
    const reset = () => setDraft(defaultDraft());
    return { draft, setDraft, reset } as const;
}

// ===== Small components =====
/* TagInput and SymbolCombo were moved inside TradeCoreRefactored so they can use the
   page-local theme-aware Input shim (and the page's isDark flag). See the inner
   definitions below the local Input/Label/Textarea shims. */
function RRCalc({ side, entry, stop, target, qty }: { side: Side; entry?: number; stop?: number; target?: number; qty?: number; }) {
    const parsed = { entry: Number(entry), stop: Number(stop), target: Number(target), qty: Number(qty) };
    const valid = [parsed.entry, parsed.stop].every((v) => Number.isFinite(v) && v > 0);
    const fullValid = valid && Number.isFinite(parsed.target) && parsed.target! > 0;
    const risk = useMemo(() => { if (!valid || parsed.qty <= 0) return undefined; const per = side === "long" ? parsed.entry - parsed.stop : parsed.stop - parsed.entry; return Math.max(0, per) * (parsed.qty || 0); }, [valid, parsed.entry, parsed.stop, parsed.qty, side]);
    const reward = useMemo(() => { if (!fullValid || parsed.qty <= 0) return undefined; const per = side === "long" ? parsed.target - parsed.entry : parsed.entry - parsed.target; return Math.max(0, per) * (parsed.qty || 0); }, [fullValid, parsed.target, parsed.entry, parsed.qty, side]);
    const rr = useMemo(() => { if (!Number.isFinite(risk) || !Number.isFinite(reward) || !risk) return undefined; return (reward as number) / (risk as number); }, [risk, reward]);
    return (
        <div className="grid grid-cols-3 gap-3 text-sm pt-2">
            <div className="rounded-2xl bg-white p-3 border border-zinc-200 dark:bg-zinc-900/40 dark:border-zinc-700">
                <div className="text-zinc-400 flex items-center gap-1"><Shield size={14} className="text-red-400" strokeWidth={2}/> Risk</div>
                <div className="text-lg font-semibold text-red-400">{fmtMoney(risk)}</div>
                <div className="mt-2 h-1 w-10 rounded bg-red-400"></div>
            </div>
            <div className="rounded-2xl bg-white p-3 border border-zinc-200 dark:bg-zinc-900/40 dark:border-zinc-700">
                <div className="text-zinc-400 flex items-center gap-1"><Target size={14} className="text-emerald-400" strokeWidth={2}/> Reward</div>
                <div className="text-lg font-semibold text-emerald-400">{fmtMoney(reward)}</div>
                <div className="mt-2 h-1 w-10 rounded bg-emerald-400"></div>
            </div>
            <div className="rounded-2xl bg-white p-3 border border-zinc-200 dark:bg-zinc-900/40 dark:border-zinc-700">
                <div className="text-zinc-400">R : R</div>
                <div className="text-lg font-semibold">{Number.isFinite(rr ?? NaN) ? (rr as number).toFixed(2) : "—"}</div>
                <div className="mt-2 h-1 w-10 rounded bg-zinc-900 dark:bg-zinc-100" />
            </div>
        </div>
    );
}

// ===== Page =====
export default function TradeCoreRefactored({ theme, onNavigate }: { theme?: 'light' | 'dark', onNavigate?: (page: PageName) => void }) {
    const { draft, setDraft, reset } = useDraft();
    const [assetVisibility] = useAssetClassVisibility();
    const isDark = typeof theme === 'string' ? theme === 'dark' : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
    const [submitting, setSubmitting] = useState(false);
    // NOTE: use plain JS ref for non-TypeScript environments
    const submitRef = useRef(null);

    const isValid = useMemo(() => draft.symbol.trim().length > 0 && Number(draft.qty) > 0 && Number(draft.entry) > 0 && Number(draft.stop) > 0, [draft]);

    const visibleAssetClassTabs = useMemo<AssetClass[]>(() => {
        const tabs: AssetClass[] = [];
        if (assetVisibility.STOCKS) tabs.push('stock');
        if (assetVisibility.FUTURES) tabs.push('futures');
        if (assetVisibility.FOREX) tabs.push('forex');

        // Options is not part of the global asset visibility model, so only expose it
        // when this is not a strict stocks-only launch configuration.
        const enabledCount = (assetVisibility.STOCKS ? 1 : 0)
            + (assetVisibility.FUTURES ? 1 : 0)
            + (assetVisibility.FOREX ? 1 : 0)
            + (assetVisibility.CRYPTO ? 1 : 0);
        if (enabledCount > 1 && assetVisibility.STOCKS) {
            tabs.push('options');
        }

        return tabs.length > 0 ? tabs : ['stock'];
    }, [assetVisibility]);

    useEffect(() => {
        if (visibleAssetClassTabs.includes(draft.assetClass)) return;
        setDraft((prev) => ({ ...prev, assetClass: visibleAssetClassTabs[0] }));
    }, [draft.assetClass, setDraft, visibleAssetClassTabs]);

    useEffect(() => {
        const onKey = (e: any) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); (submitRef.current as any)?.click?.(); }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); const tpl = NOTE_TEMPLATES["Opening Range Break"]; setDraft({ ...draft, notes: draft.notes ? draft.notes : tpl }); }
            if (e.key === "Escape") { reset(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [draft, reset, setDraft]);

    function handleSubmit() {
        if (!isValid) return;
        setSubmitting(true);
        // Persist the plan to localStorage so it appears in Plan History
        try {
            const KEY = 'plan-history-v1';
            const stored = JSON.parse(localStorage.getItem(KEY) || '[]');
            const toSave = { id: Date.now(), createdAt: new Date().toISOString(), ...draft };
            stored.unshift(toSave);
            localStorage.setItem(KEY, JSON.stringify(stored));
        } catch {
            // ignore storage errors
        }
        setTimeout(() => { setSubmitting(false); reset(); }, 600);
    }

    // Local UI shims that respect theme (override the module-level ones)
    const Card = useMemo(() => ({ children, className = "" }: any) => (
        <div className={`rounded-2xl border ${isDark ? 'border-zinc-700/60 bg-zinc-900/60 backdrop-blur-sm' : 'border-zinc-200 bg-white'} ${className}`}>{children}</div>
    ), [isDark]);
    const CardHeader = useMemo(() => ({ children, className = "" }: any) => (
        <div className={`p-5 ${isDark ? 'border-b border-zinc-700/40' : 'border-b border-zinc-200'} ${className}`}>{children}</div>
    ), [isDark]);
    const CardTitle = useMemo(() => ({ children, className = "" }: any) => (
        <h3 className={`text-lg font-semibold tracking-tight ${isDark ? 'text-zinc-100' : 'text-zinc-900'} ${className}`}>{children}</h3>
    ), [isDark]);
    const CardContent = useMemo(() => ({ children, className = "" }: any) => (
        <div className={`p-6 ${isDark ? '' : ''} ${className}`}>{children}</div>
    ), [isDark]);
    const Input = useMemo(() => ({ placeholder, value, onChange, className = "", inputMode = "text", type = "text", ...props }: any) => (
        <input type={type} placeholder={placeholder} value={value} onChange={onChange}
            className={`h-11 w-full rounded-xl px-3 text-sm ${isDark ? 'border border-zinc-700 bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500' : 'border border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500'} ${className}`} inputMode={inputMode} {...props} />
    ), [isDark]);
    const Label = useMemo(() => ({ children, className = "" }: any) => (
        <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'} ${className}`}>{children}</label>
    ), [isDark]);
    const Textarea = useMemo(() => ({ placeholder, value, onChange, rows = 10, className = "" }: any) => (
        <textarea placeholder={placeholder} value={value} onChange={onChange} rows={rows}
            className={`w-full rounded-xl px-3 py-2 text-sm ${isDark ? 'border border-zinc-700 bg-zinc-950/80 text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y' : 'border border-zinc-200 bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y'} ${className}`} />
    ), [isDark]);

    // Theme-aware TagInput and SymbolCombo are defined at module scope (ThemeTagInput /
    // ThemeSymbolCombo) so their hooks run unconditionally. They receive the page-local
    // theme-aware `Input` component and `isDark` as props.

    return (
        <div className={`${isDark ? 'min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-950 to-zinc-900 text-zinc-100 p-6' : 'min-h-screen bg-white text-zinc-900 p-6'}`}>
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
                    <h1 className="text-3xl font-bold">Trade Core and Risk Planning</h1>
                    <div className="mt-3 md:mt-0">
                        <Button variant="primary" className="uppercase rounded-xl text-sm h-9 px-3" onClick={() => { if (onNavigate) onNavigate('Plan History'); }}>
                            PLAN HISTORY
                        </Button>
                    </div>
                </div>



                {/* Two columns */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Core + Risk */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3 text-indigo-300">
                                <span className="font-sans tabular-nums text-xl font-bold bg-blue-600 w-8 h-8 inline-flex items-center justify-center rounded-full text-white leading-none shadow-md">1</span>
                                Trade Core
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            {/* Asset class tabs under Trade Core */}
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div className={`${isDark ? 'text-zinc-400' : 'text-zinc-500'} text-xs uppercase tracking-wide`}>Asset Class</div>
                                <div className={`inline-flex rounded-xl border ${isDark ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-200 bg-white'} p-1 shadow-sm`} role="tablist" aria-label="Asset Class">
                                    {visibleAssetClassTabs.map((k) => {
                                        const active = draft.assetClass === (k as any);
                                        return (
                                            <button
                                                key={k}
                                                role="tab"
                                                aria-selected={active}
                                                aria-pressed={active}
                                                onClick={() => setDraft(prev => ({ ...prev, assetClass: k as any }))}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-blue-600 text-white shadow-md' : `${isDark ? 'text-zinc-300 hover:bg-zinc-800' : 'text-zinc-700 hover:bg-zinc-100'}`}`}
                                            >
                                                {k.toUpperCase()}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label>Symbol *</Label>
                                    <ThemeSymbolCombo value={draft.symbol} onChange={(v) => setDraft({ ...draft, symbol: v })} isDark={isDark} Input={Input} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="flex items-center gap-2"><CalendarIcon size={14}/> Planned Date</Label>
                                    <Input type="date" value={draft.dateISO} onChange={(e: any) => setDraft({ ...draft, dateISO: e.target.value })} />
                                </div>

                                <div className="grid gap-2">
                                    <Label>Trade Side</Label>
                                    <div className="grid grid-cols-2 gap-3 mt-1">
                                        <Button isDark={isDark} variant={draft.side === "long" ? "success" : "secondary"} onClick={() => setDraft({ ...draft, side: "long" })} className="rounded-xl inline-flex items-center justify-center gap-2">
                                            <ArrowUpRight size={16}/> Long
                                        </Button>
                                        <Button isDark={isDark} variant={draft.side === "short" ? "destructive" : "secondary"} onClick={() => setDraft({ ...draft, side: "short" })} className="rounded-xl inline-flex items-center justify-center gap-2">
                                            <ArrowDownRight size={16}/> Short
                                        </Button>
                                    </div>
                                </div>
                                <div className="grid gap-2">
                                    <Label>Strategy (Optional)</Label>
                                    <Input placeholder="e.g., ORB, VWAP Pullback" value={draft.strategy} onChange={(e: any) => setDraft({ ...draft, strategy: e.target.value })} />
                                    <div className="text-xs text-zinc-500">Optional label for filtering. Examples: Opening Range Break, Reversal, Range Break.</div>
                                </div>
                            </div>

                            <div className={`h-px ${isDark ? 'bg-zinc-700' : 'bg-zinc-200'} my-2`} />

                            <CardTitle className="flex items-center gap-3 text-indigo-300">
                                <span className="font-sans tabular-nums text-xl font-bold bg-blue-600 w-8 h-8 inline-flex items-center justify-center rounded-full text-white leading-none shadow-md">2</span>
                                Position & Risk
                            </CardTitle>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label>Quantity *</Label>
                                    <Input inputMode="numeric" placeholder="e.g., 100" value={draft.qty as any} onChange={(e: any) => setDraft({ ...draft, qty: (e.target.value || "").replace(/[^0-9.]/g, "") })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label>Entry Price *</Label>
                                    <Input inputMode="decimal" placeholder="124.50" value={draft.entry as any} onChange={(e: any) => setDraft({ ...draft, entry: (e.target.value || "").replace(/[^0-9.]/g, "") })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-red-400 flex items-center gap-1"><Shield size={14}/> Stop Loss *</Label>
                                    <Input inputMode="decimal" placeholder="123.80" value={draft.stop as any} onChange={(e: any) => setDraft({ ...draft, stop: (e.target.value || "").replace(/[^0-9.]/g, "") })} />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-emerald-400 flex items-center gap-1"><Target size={14}/> Take Profit</Label>
                                    <Input inputMode="decimal" placeholder="126.20" value={draft.target as any} onChange={(e: any) => setDraft({ ...draft, target: (e.target.value || "").replace(/[^0-9.]/g, "") })} />
                                </div>
                            </div>

                            <RRCalc side={draft.side} entry={Number(draft.entry)} stop={Number(draft.stop)} target={Number(draft.target)} qty={Number(draft.qty)} />


                        </CardContent>
                    </Card>

                    {/* Right: Notes */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-3 text-indigo-300">
                                <span className="font-sans tabular-nums text-xl font-bold bg-blue-600 w-8 h-8 inline-flex items-center justify-center rounded-full text-white leading-none shadow-md">3</span>
                                Rationale & Tags
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            <div className="grid gap-2">
                                <Label>Tags</Label>
                                <ThemeTagInput value={draft.tags} onChange={(v) => setDraft({ ...draft, tags: v })} isDark={isDark} Input={Input} />
                            </div>
                            <div className="grid gap-2">
                                <div className="flex items-center justify-between">
                                    <Label>Notes (Pre‑Trade Rationale)</Label>
                                    <SimplePopover isDark={isDark} trigger={<Button variant="outline" className={`rounded-xl text-xs h-8 ${isDark ? 'border border-zinc-700 text-zinc-100 hover:bg-zinc-800' : 'border border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}>Insert template</Button>}>
                                        <div className="text-sm font-medium mb-2">Templates</div>
                                        <div className="grid gap-2">
                                            {Object.entries(NOTE_TEMPLATES).map(([name, tpl]) => (
                                                <button
                                                    key={name}
                                                    onClick={() => setDraft({ ...draft, notes: (draft.notes ? draft.notes + "\n\n" : "") + tpl })}
                                                    className={`text-left text-sm px-3 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-blue-50'}`}
                                                >
                                                    <div className={`${isDark ? 'font-medium text-indigo-300' : 'font-medium text-blue-600'}`}>{name}</div>
                                                    <div className={`${isDark ? 'text-xs text-zinc-500' : 'text-xs text-zinc-600'} line-clamp-2 whitespace-pre`}>{tpl}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </SimplePopover>
                                </div>
                                <Textarea rows={6} className="h-96 md:h-[440px]" placeholder="Why this trade? What is your trigger? Review after completion." value={draft.notes} onChange={(e: any) => setDraft({ ...draft, notes: e.target.value })} />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Centered action buttons (outside the cards) */}
                <div className="flex justify-center mt-6">
                    <div className="flex gap-3 flex-nowrap items-center overflow-x-auto px-2">
                        <Button ref={submitRef} variant="primary" onClick={handleSubmit} disabled={!isValid || submitting} style={{ backgroundColor: '#2563eb' }} className="bg-blue-600 hover:bg-blue-500 rounded-2xl gap-2 px-3 sm:px-6 py-2 text-sm sm:text-base text-white shadow-md hover:shadow-blue-500/30 min-w-0">
                            {submitting ? (
                                <>
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    <span className="ml-2 truncate">Saving…</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18}/>
                                    <span className="ml-2 truncate">Create Trade Plan Entry</span>
                                </>
                            )}
                        </Button>
                        <Button isDark={isDark} variant="secondary" className="rounded-2xl min-w-0" onClick={reset}><Trash2 size={16}/> <span className="ml-2 truncate">Reset All</span></Button>
                    </div>
                </div>

                {/* Footer helper (single-line, Tip bold) */}
                <Card>
                    <CardContent className="py-4 text-sm text-zinc-400">
                        <div className="flex items-center gap-4 flex-nowrap whitespace-nowrap overflow-x-auto">
                            <Info size={16} className="text-indigo-400 flex-shrink-0"/>
                            <div className="flex-shrink-0">This plan will match to your broker executions when you import history.</div>
                            <div className="flex-shrink-0"><span className="font-semibold">Tip:</span> keep tags/strategies consistent for clean filtering; store 24h internally even if you display AM/PM.</div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
